import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import LoginModal from './components/LoginModal';
import Loader from './components/Loader';
import env from './utils/env';

import './BenefitsView.css';
const BenefitsView: React.FC = () => {
    const { isFree, isAuthenticated, token, login } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'login' | 'signup'>('signup');
    
    const apiBaseUrl = env.apiUrl;

    const handleStartUpgrade = async () => {
        if (!token) {
            setError('Please log in to upgrade your account');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${apiBaseUrl}/subscription/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to create checkout session');
            }

            // Redirect to Stripe checkout
            window.location.href = data.checkoutUrl;
        } catch (err: any) {
            console.error('Upgrade error:', err);
            setError(err.message || 'Failed to start upgrade process');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container">
            <div className="benefits-page">
                <div className="hero-section">
                    <h1>Unlock the Full Potential of LuLarge</h1>
                    <p className="hero-subtitle">
                        Start with free access to explore our bond analytics platform, then
                        upgrade to Pro for unlimited access, advanced features, and priority support.
                    </p>
                </div>

                <div className="features-grid">
                    <div className="feature-card">
                        <div className="feature-icon"><span className="material-icons">search</span></div>
                        <h3>Unlimited Searches</h3>
                        <p>Perform unlimited searches across our comprehensive bond database with advanced filtering capabilities.</p>
                        <div className="feature-comparison">
                            <div className="free-limit">Free: 10 searches/day</div>
                            <div className="pro-unlimited">Pro: Unlimited</div>
                        </div>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon"><span className="material-icons">analytics</span></div>
                        <h3>Advanced Analytics</h3>
                        <p>Access detailed bond analytics, performance metrics, historical data trends, and comprehensive reports.</p>
                        <div className="feature-comparison">
                            <div className="free-limit">Free: Basic data only</div>
                            <div className="pro-unlimited">Pro: Full analytics suite</div>
                        </div>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon"><span className="material-icons">file_download</span></div>
                        <h3>Export Capabilities <span className="coming-soon-badge">Coming Soon</span></h3>
                        <p>Export search results, reports, and analytics to CSV, Excel, and PDF formats for further analysis.</p>
                        <div className="feature-comparison">
                            <div className="free-limit">Free: View only</div>
                            <div className="pro-unlimited">Pro: Full export options</div>
                        </div>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon"><span className="material-icons">support_agent</span></div>
                        <h3>Priority Support</h3>
                        <p>Get priority customer support with faster response times and dedicated assistance from our experts.</p>
                        <div className="feature-comparison">
                            <div className="free-limit">Free: Standard support</div>
                            <div className="pro-unlimited">Pro: Priority support</div>
                        </div>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon"><span className="material-icons">filter_alt</span></div>
                        <h3>Advanced Filters</h3>
                        <p>Use sophisticated filtering options to find exactly the bonds you're looking for with precision.</p>
                        <div className="feature-comparison">
                            <div className="free-limit">Free: Basic filters</div>
                            <div className="pro-unlimited">Pro: Advanced filtering</div>
                        </div>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon"><span className="material-icons">api</span></div>
                        <h3>API Access <span className="coming-soon-badge">Coming Soon</span></h3>
                        <p>Integrate LuLarge data into your own applications with our comprehensive and secure API.</p>
                        <div className="feature-comparison">
                            <div className="free-limit">Free: No API access</div>
                            <div className="pro-unlimited">Pro: Full API access</div>
                        </div>
                    </div>
                </div>

                <div className="pricing-section">
                    <div className="pricing-cards-container">
                        {/* Free Trial Card */}
                        <div className="pricing-card free-card">
                            <div className="card-badge">Most Popular</div>
                            <h2>Free Trial</h2>
                            <div className="price">
                                <span className="currency">$</span>
                                <span className="amount">0</span>
                                <span className="period">/month</span>
                            </div>
                            <div className="price-description">
                                Perfect for exploring bond research capabilities
                            </div>
                            
                            <div className="benefits-list">
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    10 searches per day
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    Basic bond data access
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    View-only reports
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    Standard support
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    Basic filtering options
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons cross">cancel</span>
                                    No API access
                                </div>
                            </div>

                            <div className="cta-section">
                                {isAuthenticated ? (
                                    isFree ? (
                                        <div className="current-plan">
                                            <span className="current-badge">Your Current Plan</span>
                                            <Link to="/content-search" className="continue-button">
                                                Continue to App
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="already-pro">
                                            <span className="downgrade-note">You have Pro access</span>
                                            <Link to="/content-search" className="continue-button">
                                                Continue to App
                                            </Link>
                                        </div>
                                    )
                                ) : (
                                    <button 
                                        className="signup-cta-button free-signup"
                                        onClick={() => {
                                            setModalMode('signup');
                                            setIsSignupModalOpen(true);
                                        }}
                                    >
                                        Start Free Trial
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Single Professional Plan */}
                        <div className="pricing-card pro-card">
                            <div className="card-badge professional">Professional</div>
                            <h2>LuLarge Pro</h2>
                            <div className="price">
                                <span className="currency">€</span>
                                <span className="amount">5000</span>
                                <span className="period">/year</span>
                            </div>
                            <div className="price-description">
                                Everything you need for professional bond research and analysis
                            </div>
                            
                            <div className="benefits-list">
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    Unlimited searches and exports
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    Advanced analytics dashboard
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    Priority customer support
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    API access for integrations
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    Historical data & trends
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    Custom reporting tools
                                </div>
                                <div className="benefit-item">
                                    <span className="material-icons check">check_circle</span>
                                    All premium features included
                                </div>
                            </div>

                            <div className="cta-section">
                                {isAuthenticated ? (
                                    isFree ? (
                                        <div className="upgrade-cta">
                                            <button 
                                                className="upgrade-now-button"
                                                onClick={handleStartUpgrade}
                                                disabled={isLoading}
                                            >
                                                {isLoading ? (
                                                    <Loader text="Processing..." />
                                                ) : (
                                                    <>
                                                        <span className="material-icons">rocket_launch</span>
                                                        Upgrade to Pro Now
                                                    </>
                                                )}
                                            </button>
                                            {error && (
                                                <div className="upgrade-error">
                                                    <span className="material-icons">warning</span>
                                                    {error}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="already-pro">
                                            <span className="pro-badge">Your Current Plan</span>
                                            <Link to="/content-search" className="continue-button">
                                                Continue to App
                                            </Link>
                                        </div>
                                    )
                                ) : (
                                    <button 
                                        className="signup-cta-button pro-signup"
                                        onClick={() => {
                                            setModalMode('signup');
                                            setIsSignupModalOpen(true);
                                        }}
                                    >
                                        Get Started with Pro
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Login prompt under both cards */}
                    <div className="auth-login-prompt">
                        <p>
                            Already registered? <button 
                                className="login-link-button"
                                onClick={() => {
                                    setModalMode('login');
                                    setIsSignupModalOpen(true);
                                }}
                            >
                                Sign in
                            </button>
                        </p>
                    </div>
                </div>

                <div className="faq-section">
                    <h2>Frequently Asked Questions</h2>
                    <div className="faq-grid">
                        <div className="faq-item">
                            <h4>Can I cancel anytime?</h4>
                            <p>Yes, you can cancel your subscription at any time. You'll continue to have Pro access until the end of your billing period.</p>
                        </div>
                        <div className="faq-item">
                            <h4>Is there a free tier?</h4>
                            <p>Yes! New users automatically get free access to explore our platform before upgrading to Pro.</p>
                        </div>
                        <div className="faq-item">
                            <h4>What payment methods do you accept?</h4>
                            <p>We accept all major credit cards (Visa, MasterCard, American Express) processed securely through Stripe.</p>
                        </div>
                        <div className="faq-item">
                            <h4>Is there enterprise pricing available?</h4>
                            <p>Contact our sales team for information about enterprise pricing options and volume discounts for larger organizations.</p>
                        </div>
                        <div className="faq-item">
                            <h4>What support is included?</h4>
                            <p>All Pro subscribers receive priority support with faster response times and dedicated assistance from our expert team.</p>
                        </div>
                    </div>
                </div>

                <div className="bottom-cta">
                    <h2>Ready to unlock Pro features?</h2>
                    <p>Join professionals who trust LuLarge for their bond research and investment decisions.</p>
                    {isAuthenticated && isFree && (
                        <div className="upgrade-cta">
                            <button 
                                className="upgrade-now-button"
                                onClick={handleStartUpgrade}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <Loader text="Processing..." />
                                ) : (
                                    <>
                                        <span className="material-icons">rocket_launch</span>
                                        Upgrade to Pro Now
                                    </>
                                )}
                            </button>
                            {error && (
                                <div className="upgrade-error">
                                    <span className="material-icons">warning</span>
                                    {error}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <LoginModal 
                isOpen={isSignupModalOpen} 
                onClose={() => setIsSignupModalOpen(false)}
                onLogin={login}
                apiBaseUrl={apiBaseUrl}
                mode={modalMode}
                onModeChange={setModalMode}
            />
        </div>
    );
};

export default BenefitsView; 